import {
  WIDGET_DEFAULT_CARD_BORDER_WIDTH,
  WIDGET_DEFAULT_CARD_RADIUS,
  WIDGET_DEFAULT_CARD_SHADOW_DIRECTIONS,
  WIDGET_DEFAULT_CARD_SHADOW_SIZE,
  WIDGET_DEFAULT_CAROUSEL_TRANSITION_DURATION_MS,
  type WidgetCardRadius,
  type WidgetCardShadowDirection,
  type WidgetCarouselTransitionDurationMs,
  type WidgetMode,
} from "./values";

export type WidgetSurfaceColorsInput = {
  mode: WidgetMode;
  backgroundColor?: string | null | undefined;
  cardColor?: string | null | undefined;
  textColor?: string | null | undefined;
};

export type WidgetCssVariablesInput = WidgetSurfaceColorsInput & {
  accentColor: string;
  fontFamily: string | null;
  cardRadius?: WidgetCardRadius | undefined;
  cardBorderWidth?: number | undefined;
  cardBorderColor?: string | null | undefined;
  cardShadowSize?: number | undefined;
  cardShadowDirections?: readonly WidgetCardShadowDirection[] | undefined;
  carouselTransitionDurationMs?: WidgetCarouselTransitionDurationMs | undefined;
};

const SHADOW_COLOR = "rgba(15, 23, 42, 0.18)";

const SHADOW_OFFSETS: Record<WidgetCardShadowDirection, (size: number) => string> = {
  top: (size) => `0 -${String(size)}px`,
  bottom: (size) => `0 ${String(size)}px`,
  left: (size) => `-${String(size)}px 0`,
  right: (size) => `${String(size)}px 0`,
};

export const buildWidgetCardShadow = (
  size: number,
  directions: readonly WidgetCardShadowDirection[],
): string => {
  if (size <= 0 || directions.length === 0) return "none";
  const blur = Math.round(size * 1.5);
  return directions
    .map((direction) => `${SHADOW_OFFSETS[direction](size)} ${String(blur)}px ${SHADOW_COLOR}`)
    .join(", ");
};

export const WIDGET_CARD_RADIUS_VALUES: Record<WidgetCardRadius, string> = {
  none: "0rem",
  sm: "0.25rem",
  md: "0.5rem",
  lg: "1rem",
  xl: "1.5rem",
};

export type WidgetSurfaceColors = {
  background: string;
  card: string;
  text: string;
  muted: string;
  border: string;
};

export const WIDGET_DEFAULT_ACCENT_COLOR = "#7c3aed";
export const WIDGET_DEFAULT_FONT_FAMILY =
  "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";

const LIGHT_PALETTE: WidgetSurfaceColors = {
  background: "#ffffff",
  card: "#f8fafc",
  text: "#1a1a1a",
  muted: "#64748b",
  border: "#e5e7eb",
};

const DARK_PALETTE: WidgetSurfaceColors = {
  background: "#ffffff",
  card: "#0f172a",
  text: "#f8fafc",
  muted: "#94a3b8",
  border: "#334155",
};

const HEX_COLOR_PATTERN = /^#([0-9a-fA-F]{6})$/;

const channelToLinear = (channel: number): number => {
  const ratio = channel / 255;
  return ratio <= 0.03928 ? ratio / 12.92 : Math.pow((ratio + 0.055) / 1.055, 2.4);
};

export const relativeLuminance = (hex: string): number | null => {
  const match = HEX_COLOR_PATTERN.exec(hex);
  if (!match || match[1] === undefined) return null;
  const value = match[1];
  const red = parseInt(value.slice(0, 2), 16);
  const green = parseInt(value.slice(2, 4), 16);
  const blue = parseInt(value.slice(4, 6), 16);
  return (
    0.2126 * channelToLinear(red) + 0.7152 * channelToLinear(green) + 0.0722 * channelToLinear(blue)
  );
};

const TEXT_ON_LIGHT_SURFACE = "#1a1a1a";
const TEXT_ON_DARK_SURFACE = "#f8fafc";
const LIGHT_SURFACE_LUMINANCE_THRESHOLD = 0.179;

export const readableTextColor = (backgroundHex: string): string => {
  const luminance = relativeLuminance(backgroundHex);
  if (luminance === null) return TEXT_ON_LIGHT_SURFACE;
  return luminance > LIGHT_SURFACE_LUMINANCE_THRESHOLD
    ? TEXT_ON_LIGHT_SURFACE
    : TEXT_ON_DARK_SURFACE;
};

export const resolveWidgetSurfaceColors = (
  input: WidgetSurfaceColorsInput,
): WidgetSurfaceColors => {
  const palette = input.mode === "dark" ? DARK_PALETTE : LIGHT_PALETTE;
  return {
    ...palette,
    background: input.backgroundColor ?? palette.background,
    card: input.cardColor ?? palette.card,
    text: input.textColor ?? palette.text,
  };
};

export const buildWidgetCssVariables = (input: WidgetCssVariablesInput): Record<string, string> => {
  const colors = resolveWidgetSurfaceColors(input);
  return {
    "--wall-accent": input.accentColor,
    "--wall-font": input.fontFamily ?? WIDGET_DEFAULT_FONT_FAMILY,
    "--wall-bg": colors.background,
    "--wall-bg-fg": readableTextColor(colors.background),
    "--wall-fg": colors.text,
    "--wall-muted": colors.muted,
    "--wall-border": colors.border,
    "--wall-surface": colors.card,
    "--wall-card-radius": WIDGET_CARD_RADIUS_VALUES[input.cardRadius ?? WIDGET_DEFAULT_CARD_RADIUS],
    "--wall-card-border-width": `${String(input.cardBorderWidth ?? WIDGET_DEFAULT_CARD_BORDER_WIDTH)}px`,
    "--wall-card-border-color": input.cardBorderColor ?? colors.border,
    "--wall-card-shadow": buildWidgetCardShadow(
      input.cardShadowSize ?? WIDGET_DEFAULT_CARD_SHADOW_SIZE,
      input.cardShadowDirections ?? WIDGET_DEFAULT_CARD_SHADOW_DIRECTIONS,
    ),
    "--wall-carousel-duration": `${String(
      input.carouselTransitionDurationMs ?? WIDGET_DEFAULT_CAROUSEL_TRANSITION_DURATION_MS,
    )}ms`,
  };
};
